import { CSSResultGroup, LitElement, html, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './feature-flag.style.js'
import { fetcher } from 'components/shared/fetcher.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import {
  parseFeatureFlags,
  type FeatureFlag,
  type FeatureFlagOption,
  type FeatureFlagSource,
  type FeatureFlagsPayload,
} from './feature-flag-parser.js'


export const FEATURE_FLAGS_ELEMENT_ID = 'mosaic-flags'
const FLAG_PARAM_PREFIX = 'ff_'

const sourceLabels = {
  default: () => t('source_default', { ns: 'csc-feature-flag', default: 'Default' }),
  shop: () => t('source_shop', { ns: 'csc-feature-flag', default: 'Theme setting' }),
  session: () => t('source_session', { ns: 'csc-feature-flag', default: 'Session override' }),
} satisfies Record<FeatureFlagSource, () => unknown>

const readPayload = (): FeatureFlagsPayload | undefined => {
  const element = document.getElementById(FEATURE_FLAGS_ELEMENT_ID)
  if (!element?.textContent) return undefined

  try {
    return parseFeatureFlags(JSON.parse(element.textContent))
  } catch (error) {
    console.error(error)
    return undefined
  }
}

@customElement('csc-feature-flag')
export class CSCFeatureFlag extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @state()
  private payload: FeatureFlagsPayload | undefined

  @state()
  private resetting = false

  @inject(WINDOW_LOCATION)
  protected windowLocation!: WindowLocation

  override connectedCallback() {
    super.connectedCallback()
    this.payload = readPayload()
  }

  private resetAll = async () => {
    if (!this.payload) return

    this.resetting = true
    try {
      const url = await createUrl(this.payload.reset_url, { fromOrigin: true })
      const response = await fetcher.fetch(url, { method: 'DELETE' })
      if (response.ok) {
        this.windowLocation.redirect(await this.pageWithoutFlagParams(this.payload.page))
        return
      }
      console.error(`Resetting session flags failed with status ${response.status}`)
    } catch (error) {
      console.error(error)
    }
    this.resetting = false
  }

  private async pageWithoutFlagParams(page: string) {
    const url = await createUrl(page, { fromOrigin: true })
    const flagParams = [...url.searchParams.keys()].filter(key => key.startsWith(FLAG_PARAM_PREFIX))
    flagParams.forEach(key => url.searchParams.delete(key))
    return url
  }

  private renderValues(options: FeatureFlagOption[]) {
    return html`
      <cse-button-row>
        ${options.map(option => html`
          <cse-button
            size="small"
            url=${option.url}
            variant=${option.active ? 'success' : 'default'}
            aria-label=${ifDefined(option.active ? t('option_active', { ns: 'csc-feature-flag', default: '{{variable_value}}, active', variable_value: option.value }) : undefined)}
          >${option.value}</cse-button>
        `)}
      </cse-button-row>
    `
  }

  private renderFlag(flag: FeatureFlag) {
    return html`
      <cse-group direction="column" row-gap="02" class="flag">
        <cse-group column-gap="06" alignment="center">
          <cse-text variant="heading-5">${flag.name}</cse-text>
          <cse-badge size="small" variant=${flag.source === 'session' ? 'info' : 'default'}>
            ${sourceLabels[flag.source]()}
          </cse-badge>
          ${flag.source === 'session' ? html`
            <cse-link url=${flag.reset_url} class="reset">${t('reset', { ns: 'csc-feature-flag', default: 'Reset' })}</cse-link>
          ` : nothing}
        </cse-group>
        <cse-text variant="caption-m">${flag.description}</cse-text>
        ${this.renderValues(flag.options)}
      </cse-group>
    `
  }

  private renderFlags(flags: FeatureFlag[]) {
    if (!flags.length) return nothing

    return html`
      <cse-accordion open variant="panel" class="flags">
        <cse-text slot="title" as="h3" variant="heading-4">${t('flags_title', { ns: 'csc-feature-flag', default: 'Feature flags' })}</cse-text>
        <cse-group slot="content" direction="column">
          ${flags.map((flag, index) => html`
            ${index !== 0 ? html`<cse-divider></cse-divider>` : nothing}
            ${this.renderFlag(flag)}
          `)}
        </cse-group>
      </cse-accordion>
    `
  }

  private renderPreview({ options, wildcard, revealed, reset_url }: FeatureFlagsPayload['preview']) {
    if (!options.length) return nothing

    return html`
      <cse-accordion open variant="panel" class="preview">
        <cse-text slot="title" as="h3" variant="heading-4">${t('preview_title', { ns: 'csc-feature-flag', default: 'Preview flags' })}</cse-text>
        <cse-group slot="content" direction="column" row-gap="02">
          <cse-group column-gap="06" alignment="center">
            <cse-text variant="heading-5">${t('preview_features', { ns: 'csc-feature-flag', default: 'Preview features on this page' })}</cse-text>
            ${wildcard ? html`
              <cse-badge size="small" variant="info">${t('preview_all', { ns: 'csc-feature-flag', default: 'All' })}</cse-badge>
            ` : nothing}
            ${revealed.length ? html`
              <cse-link url=${reset_url} class="reset">${t('reset', { ns: 'csc-feature-flag', default: 'Reset' })}</cse-link>
            ` : nothing}
          </cse-group>
          ${this.renderValues(options)}
        </cse-group>
      </cse-accordion>
    `
  }

  override render() {
    if (!this.payload) return nothing

    const { flags, preview } = this.payload
    const hasOverrides = flags.some(flag => flag.source === 'session') || preview.revealed.length > 0

    return html`
      <cse-group direction="column">
        <cse-accordion-group multiopen>
          ${this.renderFlags(flags)}
          ${this.renderPreview(preview)}
        </cse-accordion-group>
        ${hasOverrides ? html`
          <cse-button class="reset-all" variant="secondary" ?loading=${this.resetting} @click=${this.resetAll}>
            ${t('reset_all', { ns: 'csc-feature-flag', default: 'Remove all session overrides' })}
          </cse-button>
        ` : nothing}
      </cse-group>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-feature-flag': CSCFeatureFlag
  }
}
